// Catalog editor helpers: edit Shopify variants and products from anywhere in the UI. Changes show
// at once; the server queues them for Shopify (shopify_write) and HHL Ops re-reads the product
// once Shopify has them.
import type { Item, ShopifyProduct } from "../../../shared/db.gen.ts";
import type { MutationArgs } from "../../../shared/mutations.ts";
import { mutateWithResult } from "./mutate.ts";
import { type Change, store } from "./sync.ts";

export type VariantPatch = MutationArgs<"shopify.edit_variants">["edits"][number]["patch"];
export type ProductPatch = MutationArgs<"shopify.edit_products">["edits"][number]["patch"];

const variantName = (productTitle: string, variantTitle: string) => (variantTitle ? `${productTitle} - ${variantTitle}` : productTitle);

export async function editVariants(edits: { item_id: string; patch: VariantPatch }[]) {
  const changes: Change[] = [];
  for (const e of edits) {
    const row = store.row("item", e.item_id);
    if (row) changes.push({ t: "item", id: e.item_id, row: { ...row, ...e.patch } });
  }
  return mutateWithResult("shopify.edit_variants", { edits }, changes);
}

export async function editProducts(edits: { product_id: string; patch: ProductPatch }[]) {
  const changes: Change[] = [];
  const items = store.rows("item") as unknown as Item[];
  for (const e of edits) {
    const row = store.row("shopify_product", e.product_id) as unknown as ShopifyProduct | undefined;
    if (!row) continue;
    const next = { ...row, ...e.patch };
    changes.push({ t: "shopify_product", id: e.product_id, row: next as never });
    if (e.patch.title !== undefined || e.patch.status !== undefined || e.patch.vendor !== undefined) {
      for (const i of items) {
        if (i.shopify_product_id !== e.product_id) continue;
        changes.push({
          t: "item",
          id: i.id,
          row: {
            ...i,
            shopify_product_title: next.title,
            name: variantName(next.title, i.shopify_variant_title),
            shopify_status: next.status,
            shopify_vendor: next.vendor,
            archived: next.status === "ARCHIVED",
          } as never,
        });
      }
    }
  }
  return mutateWithResult("shopify.edit_products", { edits }, changes);
}
